import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { fetchData } from '../../../../helpers/fetchData';
import CustomTable from '../../../../shared/CustomTable';
import CustomPagination from '../../../../shared/CustomPagination';
import CustomButton from '../../../../shared/CustomButton';
import DropdownField from '../../../../shared/DropdownField';
import DatePicker from '../../../../shared/DatePicker';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import CheckPermission from '../../../../hooks/CheckPermission';
import { PrintIcon } from '../../../../utils/TableActionIcons';
import { formatDateTime } from '../../../../utils/FormateDateTime';
import { FaExclamationCircle } from 'react-icons/fa';
import { HiArrowUturnLeft } from 'react-icons/hi2';
import ReusableModal from '../../../../shared/ReuseableModal';
import PayerCreditInvoiceForm from './PayerCreditInvoiceForm';
import { useGetInsuranceClaimCreditInvoicesQuery } from '../../../../redux/api/modules/billingApi';

const InsuranceClaimCreditInvoice = () => {
    const { hasPermission } = CheckPermission();
    const { t } = useTranslation();
    const navigate = useNavigate();
    // Pagination and Filters State
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: 20
    });

    const [filters, setFilters] = useState({
        payer_id: '',
        branch_id: '',
        start_date: moment().format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        query: ''
    });
    const [appliedFilters, setAppliedFilters] = useState(filters);

    const queryPayload = React.useMemo(() => {
        const payload = {
            payer_id: appliedFilters.payer_id || undefined,
            branch_id: appliedFilters.branch_id || undefined,
            start_date: appliedFilters.start_date,
            end_date: appliedFilters.end_date,
            page: pagination.page,
            per_page: pagination.per_page
        };
        Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
        return payload;
    }, [appliedFilters, pagination.page, pagination.per_page]);

    const {
        data: responseData,
        isLoading: queryLoading,
        isFetching: queryFetching,
        refetch
    } = useGetInsuranceClaimCreditInvoicesQuery(queryPayload);

    const loading = queryLoading || queryFetching;
    const data = responseData?.data || [];

    const [payers, setPayers] = useState([]);
    const [payerRecords, setPayerRecords] = useState([]);
    const [openCreditInvoiceModal, setOpenCreditInvoiceModal] = useState(false);
    const [creditInvoiceLoading, setCreditInvoiceLoading] = useState(false);
    const [failedInvoiceData, setFailedInvoiceData] = useState(null);

    useEffect(() => {
        fetchPayers();
    }, []);

    useEffect(() => {
        const openCreditInvoiceModal = () => setOpenCreditInvoiceModal(true);
        window.addEventListener('openInsuranceClaimCreditInvoiceModal', openCreditInvoiceModal);

        return () => {
            window.removeEventListener('openInsuranceClaimCreditInvoiceModal', openCreditInvoiceModal);
        };
    }, []);

    const fetchPayers = async () => {
        try {
            const res = await fetchData('POST', '/v3/payer/get', { category: '' });
            if (res?.data) {
                setPayerRecords(res.data);
                setPayers(res.data.map(p => ({
                    label: p.name || p.name_en || p.payer_name,
                    value: p.payer_id,
                    payer: p
                })));
            }
        } catch (error) {
            console.error('Error fetching payers:', error);
        }
    };

    const handleApplyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        setAppliedFilters(filters);
    };

    const handleResetFilters = () => {
        const resetFilters = {
            payer_id: '',
            branch_id: '',
            start_date: moment().format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
            query: ''
        };
        setFilters(resetFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
        setAppliedFilters(resetFilters);
    };

    const roundAmount = value => Number((Number(value || 0)).toFixed(2));

    const calculateCreditInvoiceTotals = invoiceDetails => {
        return invoiceDetails.reduce((totals, item) => {
            const quantity = Number(item.quantity || 0);
            const unitPrice = Number(item.unit_price || 0);
            const discount = Number(item.discount || 0);
            const gross = unitPrice * quantity;
            const itemDiscount = discount * quantity;
            const vat = item.vat ? Number(item.total_vat || 0) : 0;
            const net = Math.max(0, gross - itemDiscount);

            return {
                total_gross: totals.total_gross + gross,
                total_discount: totals.total_discount + itemDiscount,
                total_vat: totals.total_vat + vat,
                total_net: totals.total_net + net,
                company_share: totals.company_share + net,
                company_share_with_vat: totals.company_share_with_vat + net + vat
            };
        }, {
            total_gross: 0,
            total_discount: 0,
            total_vat: 0,
            total_net: 0,
            company_share: 0,
            company_share_with_vat: 0
        });
    };

    const handleCreateCreditInvoice = async values => {
        if (!hasPermission('billing', 'create')) return;
        const selectedPayer = payerRecords.find(p => p.payer_id === values.payer_id);
        if (!selectedPayer) {
            toast.error(t('Payer is required'));
            return;
        }

        setCreditInvoiceLoading(true);
        try {
            const returnedItems = values.invoice_details.map(item => ({
                ...item,
                quantity: Number(item.quantity || 0),
                unit_price: Number(item.unit_price || 0),
                discount: Number(item.discount || 0),
                total_vat: item.vat ? Number(item.total_vat || 0) : 0
            }));
            const totals = calculateCreditInvoiceTotals(returnedItems);
            const payload = {
                payer: selectedPayer,
                return_reason: values.reason,
                items: returnedItems,
                date: moment(values.date).format('YYYY-MM-DDTHH:mm:ss[Z]'),
                invoice_type: selectedPayer.category || 'insurance',
                total_gross: roundAmount(totals.total_gross),
                total_discount: roundAmount(totals.total_discount),
                total_vat: roundAmount(totals.total_vat),
                total_deduction: 0,
                company_share: roundAmount(totals.company_share),
                total_net: roundAmount(totals.total_net),
                company_share_with_vat: roundAmount(totals.company_share_with_vat),
                total_gsa_amount: roundAmount(totals.company_share_with_vat)
            };

            const res = await fetchData(
                'POST',
                '/v3/invoices/insurance_claim_invoices/credit',
                payload
            );

            if (res) {
                toast.success(res?.message || t('Credit company invoice created successfully'));
                setOpenCreditInvoiceModal(false);
                refetch();
            }
        } catch (error) {
            toast.error(error?.message || t('Failed to create credit invoice'));
        } finally {
            setCreditInvoiceLoading(false);
        }
    };

    const columns = [
        {
            header: t('Actions'),
            body: (row) => (
                <div className="flex gap-2">
                    <PrintIcon
                        title={t('Print')}
                        iconSize={17}
                        onClick={() => navigate('/billing/insurance-claim-credit-company-invoice/print', {
                            state: {
                                invoiceId: row.id,
                                invoiceData: row,
                                path: '/billing',
                                module: 6
                            }
                        })}
                    />
                    {row?.zatca_status === 'FAILED' && (
                        <FaExclamationCircle
                            title={t('View Failure Reason')}
                            className="text-red-600 cursor-pointer hover:text-red-700"
                            size={17}
                            onClick={() => setFailedInvoiceData(row)}
                        />
                    )}
                </div>
            )
        },
        {
            header: t('Invoice Number'),
            body: (row) => row?.invoice_no || '-'
        },
        {
            header: t('Date'),
            body: (row) => formatDateTime(row?.date)
        },
        {
            header: t('Payer'),
            body: (row) => row?.payer?.name_en || row?.payer?.name || row?.payer_snapshot?.name_en || row?.payer_snapshot?.name || '-'
        },
        {
            header: t('Gross'),
            body: (row) => Number(row?.total_gross || 0).toFixed(2)
        },
        {
            header: t('Discount'),
            body: (row) => Number(row?.total_discount || 0).toFixed(2)
        },
        {
            header: t('Company Share'),
            body: (row) => Number(row?.company_share || 0).toFixed(2)
        },
        {
            header: t('VAT'),
            body: (row) => Number(row?.total_vat || 0).toFixed(2)
        },
        {
            header: t('Net with VAT'),
            body: (row) => Number(row?.company_share_with_vat || 0).toFixed(2)
        },
        {
            header: t('Return Reason'),
            body: (row) => row?.return_reason || '-'
        },
    ];

    return (
        <div className="w-full">
            <div className="flex gap-3 mb-4 flex-wrap items-start">
                <div className="flex-none w-[180px]">
                    <DatePicker
                        placeholder={t('StartDate')}
                        value={filters.start_date ? new Date(filters.start_date) : null}
                        onChange={(e) => setFilters(prev => ({ ...prev, start_date: moment(e.value).format('YYYY-MM-DD') }))}
                        useFormik={false}
                    />
                </div>
                <div className="flex-none w-[180px]">
                    <DatePicker
                        placeholder={t('EndDate')}
                        value={filters.end_date ? new Date(filters.end_date) : null}
                        onChange={(e) => setFilters(prev => ({ ...prev, end_date: moment(e.value).format('YYYY-MM-DD') }))}
                        useFormik={false}
                    />
                </div>
                <div className="flex-none w-[180px]">
                    <DropdownField
                        placeholder={t('Payer')}
                        options={payers}
                        value={filters.payer_id}
                        onChange={(e) => setFilters(prev => ({ ...prev, payer_id: e.value }))}
                        useFormik={false}
                        filter
                    />
                </div>
                <div className="flex-none flex gap-2">
                    <CustomButton
                        title={t('ApplyFilters')}
                        icon="pi pi-filter"
                        buttonType="primary"
                        onClick={handleApplyFilters}
                        className="w-fit h-[35px]"
                    />
                    <CustomButton
                        title={t('Reset')}
                        icon="pi pi-sync"
                        buttonType="reset"
                        onClick={handleResetFilters}
                        className="w-fit h-[35px]"
                    />
                </div>
            </div>

            <CustomTable
                isLoading={loading}
                data={data}
                columns={columns}
            />

            {data?.length > 0 && !loading && (
                <CustomPagination
                    currentPage={responseData?.page || pagination.page}
                    rowsPerPage={responseData?.per_page || pagination.per_page}
                    totalRecords={responseData?.total_record || responseData?.totalRecords || 0}
                    totalPages={responseData?.total_pages || responseData?.totalPages || 1}
                    onPageChange={(page, per_page) => {
                        setPagination(prev => ({ ...prev, page, per_page }));
                    }}
                />
            )}

            <ReusableModal
                isVisible={openCreditInvoiceModal}
                title={t('Create Credit Company Invoice')}
                HeaderIcon={HiArrowUturnLeft}
                position="top"
                className="w-full md:w-[90%]"
                onClose={() => {
                    setOpenCreditInvoiceModal(false);
                    setCreditInvoiceLoading(false);
                }}
            >
                <PayerCreditInvoiceForm
                    handleSubmit={handleCreateCreditInvoice}
                    loading={creditInvoiceLoading}
                    payers={payers}
                />
            </ReusableModal>

            <ReusableModal
                isVisible={!!failedInvoiceData}
                title={t('Failure Reason')}
                HeaderIcon={FaExclamationCircle}
                position="top"
                className="w-full md:w-[620px]"
                onClose={() => setFailedInvoiceData(null)}
            >
                <div className="space-y-3">
                    <div className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-800">
                            {t('Invoice Number')}:
                        </span>{' '}
                        {failedInvoiceData?.invoice_no || '-'}
                    </div>
                    <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800 whitespace-pre-wrap break-words">
                        {failedInvoiceData?.zatca_error_message ||
                            t('No failure reason provided')}
                    </div>
                </div>
            </ReusableModal>
        </div>
    );
};

export default InsuranceClaimCreditInvoice;
